/**
 * Alert Utilities - Client-side alert enrichment and grouping
 * Works with Phase 2 backend alert data structure (database persistence)
 * Handles both Phase 1 (on-demand) and Phase 2 (pre-calculated) API responses
 */

// ==================== ALERT ENRICHMENT ====================

/**
 * Enrich backend alert with client-side status and scope
 *
 * PHASE 4.5 UPDATE: Backend now provides transformed fields:
 * - category: Pre-calculated from alert type (Maintenance, Compliance & Coverage, etc.)
 * - type_display: Human-readable type name (PPM, Calibration, Warranty, Contract)
 * - days_overdue: Pre-calculated days difference from due date
 *
 * This function now only adds minimal client-side fields:
 * - status: For view filtering (active, acknowledged, snoozed, archived)
 * - scope: For dashboard/page context
 *
 * @param {Object} backendAlert - Alert data from backend (now includes category, type_display, days_overdue)
 * @returns {Object} Enriched alert with minimal client-side additions
 */
export const enrichAlert = (backendAlert) => {
  // Normalize severity (Critical/Warning/Info)
  const severityRaw = backendAlert.severity || backendAlert.priority || 'info';
  const severity = String(severityRaw).toLowerCase();

  // Status from database or default to 'active'
  const status = backendAlert.status || 'active';

  return {
    ...backendAlert,
    severity: severity,
    // Status for view filtering (active, acknowledged, snoozed, archived)
    status: status,
    // Client-side fields (database already provides category, type_display, days_overdue)
    acknowledged_at: backendAlert.acknowledged_at || null,
    snoozed_until: backendAlert.snoozed_until || null,
    scope: backendAlert.subject_type || (backendAlert.device_id ? 'device' : 'other'),
    subject_type: backendAlert.subject_type,
    subject_id: backendAlert.subject_id,
    // NOTE: 'category' is now provided by backend
    // NOTE: 'type_display' is now provided by backend
    // NOTE: 'days_overdue' is now provided by backend
    // NOTE: 'available_actions' removed - not used in UI
  };
};

/** True when alert is tied to a device (directly or via complaint). */
export const isDeviceLinkedAlertSubject = (alert) => {
  const subjectType = alert?.subject_type;
  return subjectType === 'device' || subjectType === 'complaint';
};

/** True when alert subject is a location (e.g. budget overrun). */
export const isLocationAlertSubject = (alert) => alert?.subject_type === 'location';

/** Timestamp for list display, sort, and timeline grouping (last job detection). */
export const getAlertDisplayTimestamp = (alert) =>
  alert?.last_detected_at || alert?.created_at || null;

/** Client sort key mirroring backend subject_primary CASE. */
export const getSubjectPrimarySortKey = (alert) => {
  const t = alert?.subject_type;
  if (t === 'device') {
    return (alert.device_name || alert.device_identifier || '').toLowerCase();
  }
  if (t === 'complaint') {
    return (alert.complaint_identifier || '').toLowerCase();
  }
  if (t === 'location') {
    return (alert.location_name || '').toLowerCase();
  }
  return (alert.subject_display_name || alert.location_name || '').toLowerCase();
};

/**
 * Format alert type key for display: calibration_due_soon → Calibration Due Soon
 */
export const formatAlertTypeLabel = (type) => {
  if (!type) return '';
  return String(type)
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// ==================== ALERT GROUPING ====================

/**
 * Group alerts by specified dimension
 * @param {Array} alerts - List of alerts
 * @param {string} groupBy - Grouping method: 'timeline', 'category', 'severity', 'device', 'department'
 * @returns {Object} Grouped alerts with keys as group names
 */
export const groupAlerts = (alerts, groupBy = 'timeline') => {
  switch (groupBy) {
    case 'timeline':
      return groupByTimeline(alerts);
    case 'category':
      return groupByCategory(alerts);
    case 'severity':
      return groupBySeverity(alerts);
    case 'device':
      return groupByDevice(alerts);
    case 'department':
      return groupByDepartment(alerts);
    default:
      return { 'All': alerts };
  }
};

/**
 * Group alerts by timeline (Today, This Week, Earlier)
 * @param {Array} alerts
 * @returns {Object}
 */
const groupByTimeline = (alerts) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const grouped = {
    'Today': [],
    'This Week': [],
    'Earlier': [],
  };

  alerts.forEach(alert => {
    const alertDate = new Date(getAlertDisplayTimestamp(alert));
    if (alertDate >= today) {
      grouped['Today'].push(alert);
    } else if (alertDate >= weekAgo) {
      grouped['This Week'].push(alert);
    } else {
      grouped['Earlier'].push(alert);
    }
  });

  // Remove empty groups
  return Object.fromEntries(
    Object.entries(grouped).filter(([_, items]) => items.length > 0)
  );
};

/**
 * Group alerts by category (Maintenance, Compliance & Coverage, etc.)
 * PHASE 4.5 UPDATE: Now uses backend-provided category field instead of UI deriving it.
 * @param {Array} alerts
 * @returns {Object}
 */
const groupByCategory = (alerts) => {
  return alerts.reduce((acc, alert) => {
    // Use category provided by backend (no need to re-derive)
    const category = alert.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(alert);
    return acc;
  }, {});
};

/**
 * Group alerts by priority (Critical, Warning, Info)
 * @param {Array} alerts
 * @returns {Object}
 */
const groupByPriority = (alerts) => {
  return {
    'Critical': alerts.filter(a => a.severity === 'critical'),
    'Warning': alerts.filter(a => a.severity === 'warning'),
    'Info': alerts.filter(a => a.severity === 'info'),
  };
};

const groupBySeverity = groupByPriority;

/**
 * Group alerts by device
 * @param {Array} alerts
 * @returns {Object}
 */
const groupByDevice = (alerts) => {
  const grouped = {};

  alerts.forEach(alert => {
    // Create display label with device name and identifier
    const displayLabel = alert.device_identifier
      ? `${alert.device_name || 'Unknown'} (${alert.device_identifier})`
      : alert.device_name || 'Unknown Device';

    if (!grouped[displayLabel]) {
      grouped[displayLabel] = [];
    }
    grouped[displayLabel].push(alert);
  });

  // Sort groups by device name alphabetically
  return Object.keys(grouped)
    .sort()
    .reduce((sorted, key) => {
      sorted[key] = grouped[key];
      return sorted;
    }, {});
};

/**
 * Group alerts by department
 * @param {Array} alerts
 * @returns {Object}
 */
const groupByDepartment = (alerts) => {
  const grouped = {};

  alerts.forEach(alert => {
    // Use department_name as key, with icon if available
    const departmentKey = alert.department_name || 'Unassigned Department';
    const displayLabel = alert.department_icon
      ? `${alert.department_icon} ${departmentKey}`
      : departmentKey;

    if (!grouped[displayLabel]) {
      grouped[displayLabel] = [];
    }
    grouped[displayLabel].push(alert);
  });

  // Sort groups by department name alphabetically (after emoji)
  return Object.keys(grouped)
    .sort((a, b) => {
      const nameA = a.replace(/^[^\s]\s/, '').toLowerCase();
      const nameB = b.replace(/^[^\s]\s/, '').toLowerCase();
      return nameA.localeCompare(nameB);
    })
    .reduce((sorted, key) => {
      sorted[key] = grouped[key];
      return sorted;
    }, {});
};

// ==================== ALERT SORTING ====================

/**
 * Sort alerts by priority and date
 * @param {Array} alerts
 * @returns {Array} Sorted alerts
 */
export const sortAlerts = (alerts) => {
  const severityOrder = { critical: 0, warning: 1, info: 2 };

  return [...alerts].sort((a, b) => {
    // Sort by severity first
    const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
    if (severityDiff !== 0) return severityDiff;

    // Then by date (newest first)
    return new Date(getAlertDisplayTimestamp(b)) - new Date(getAlertDisplayTimestamp(a));
  });
};

// ==================== LOCALSTORAGE MANAGEMENT ====================

/**
 * Get stored alert status from localStorage
 * @param {string} alertId
 * @returns {Object} Alert status data or empty object
 */
export const getStoredAlertStatus = (alertId) => {
  try {
    const stored = localStorage.getItem(`alert_${alertId}`);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading alert status from localStorage:', error);
    return {};
  }
};

/**
 * Save alert status to localStorage
 * @param {string} alertId
 * @param {Object} statusUpdate - { status, acknowledged_at, snoozed_until, etc. }
 */
export const saveAlertStatus = (alertId, statusUpdate) => {
  try {
    const existing = getStoredAlertStatus(alertId);
    const updated = { ...existing, ...statusUpdate };
    localStorage.setItem(`alert_${alertId}`, JSON.stringify(updated));
  } catch (error) {
    console.error('Error saving alert status to localStorage:', error);
  }
};

/**
 * Clear all stored alert statuses (for cleanup)
 */
export const clearAllAlertStatuses = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith('alert_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('Error clearing alert statuses:', error);
  }
};

// ==================== ALERT COUNTING ====================

/**
 * Calculate alert counts by priority
 * @param {Array} alerts
 * @returns {Object} { critical, warning, info, total }
 */
export const calculateAlertCounts = (alerts) => {
  // Only count active alerts (not archived)
  const activeAlerts = alerts.filter(a => a.status !== 'archived');

  return {
    critical: activeAlerts.filter(a => a.severity === 'critical').length,
    warning: activeAlerts.filter(a => a.severity === 'warning').length,
    info: activeAlerts.filter(a => a.severity === 'info').length,
    total: activeAlerts.length,
  };
};

// ==================== TIME UTILITIES ====================

/**
 * Get human-readable relative time (e.g., "2 hours ago")
 * @param {string|Date} dateString
 * @returns {string}
 */
export const getRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString();
};

/**
 * Check if snoozed alert should be shown again
 * @param {Object} alert
 * @returns {boolean}
 */
export const shouldShowSnoozedAlert = (alert) => {
  if (alert.status !== 'snoozed' || !alert.snoozed_until) {
    return true;
  }

  const snoozedUntil = new Date(alert.snoozed_until);
  return new Date() >= snoozedUntil;
};
